import { create } from 'zustand';
import { doDELETE, doGET, doPOST, doPUT } from '../../utils/HttpUtils';
import { showError, showSuccess } from '../toaster';
import { UpdateUserData, UserData, UserFilters, UserState } from '../../types/user';
import { downloadCsvFile } from '../../utils/export';
import { ICorporate } from '../../types/corporates';
import { omitEmptyOptionalFields } from '../../utils/helper';

const BASE = '/users';

const normalizeUsers = (response: any): UserData[] => {
    const rootPayload = response?.data ?? {};
    const payload = rootPayload?.data ?? rootPayload ?? {};

    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.users)) return payload.users;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(rootPayload?.users)) return rootPayload.users;
    if (Array.isArray(rootPayload?.rows)) return rootPayload.rows;
    if (Array.isArray(rootPayload?.results)) return rootPayload.results;

    return [];
};

const normalizeCorporates = (response: any): ICorporate[] => {
    const rootPayload = response?.data ?? {};
    const payload = rootPayload?.data ?? rootPayload ?? {};

    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.corporates)) return payload.corporates;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(rootPayload?.corporates)) return rootPayload.corporates;

    return [];
};

const buildExportRows = (users: UserData[]) =>
    users.map((user, index) => [
        user.name || '',
        user.email || '',
        user.phone || '',
    ]);

const formatCouponUsage = (couponResponse: any) => {
    const coupons = couponResponse?.data?.data?.coupons;

    if (!Array.isArray(coupons) || !coupons.length) {
        return '';
    }

    return coupons
        .map((coupon: any) => {
            const code = coupon?.couponCode || '';
            const amount = typeof coupon?.totalDiscountAmount === 'number'
                ? coupon.totalDiscountAmount
                : '';

            return code ? `${code}(${amount})` : '';
        })
        .filter(Boolean)
        .join(', ');
};

const store = create<UserState>((set, get) => ({
    data: [],
    totalPages: 0,
    currentPage: 1,
    filters: {},
    isLoading: false,
    rows: 10,
    total: 0,
    allData: [],

    fetchGrid: async () => {
        try {
            const { filters, currentPage, rows, isLoading } = get();
            if (isLoading) return;

            set({ isLoading: true });

            const pageParams = new URLSearchParams(filters as any);
            pageParams.append('page', String(currentPage));
            pageParams.append('limit', String(rows));

            const pageResponse = await doGET(`${BASE}?${pageParams.toString()}`);

            if (pageResponse.status >= 200 && pageResponse.status < 400) {
                const pagedUsers = normalizeUsers(pageResponse.data);
                const offset = (currentPage - 1) * rows;
                const hasPossibleNextPage = pagedUsers.length === rows;
                const totalCount = hasPossibleNextPage
                    ? offset + pagedUsers.length + 1
                    : offset + pagedUsers.length;
                const totalPagesCount = hasPossibleNextPage
                    ? currentPage + 1
                    : Math.max(currentPage, 1);

                set({
                    allData: [],
                    data: pagedUsers,
                    totalPages: totalPagesCount,
                    total: totalCount,
                });
            } else {
                showError(pageResponse.message || 'Failed to fetch users');
            }
        } catch (err) {
            showError('Failed to fetch users');
        } finally {
            set({ isLoading: false });
        }
    },

    setFilters: (newFilters: UserFilters) => {
        set({ filters: newFilters, currentPage: 1 });
        get().fetchGrid();
    },

    nextPage: () => {
        const { currentPage, totalPages, fetchGrid } = get();
        if (currentPage < totalPages) {
            set(state => ({ currentPage: state.currentPage + 1 }));
            fetchGrid();
        }
    },

    prevPage: () => {
        const { currentPage, fetchGrid } = get();
        if (currentPage > 1) {
            set(state => ({ currentPage: state.currentPage - 1 }));
            fetchGrid();
        }
    },

    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        const { totalPages } = get();
        const newPage = page + 1;
        if (newPage >= 1 && newPage <= totalPages) {
            set({ currentPage: newPage });
            get().fetchGrid();
        }
    },

    onCreate: async (data: UserData) => {
        try {
            const payload = omitEmptyOptionalFields({ ...data });
            const response = await doPOST(BASE, payload);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
                showSuccess("User created successfully");
                return response?.data?.data;
            } else {
                showError(response.message || 'Failed to create user');
            }
            return null;
        } catch (error) {
            showError('Error creating user');
            return null;
        }
    },

    onUpdate: async (data: UpdateUserData) => {
        try {
            const id = data._id;
            if (!id) {
                showError('User ID is required for update');
                return null;
            }
            const payload = {
                ...omitEmptyOptionalFields({ ...data }),
                ...(data.corporateId === null ? { corporateId: null } : {}),
                _id: id,
            };
            const response = await doPUT(`${BASE}/${id}`, payload);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
                showSuccess("User updated successfully");
                return response?.data?.data;
            } else {
                showError(response.message || 'Failed to update user');
            }
            return null;
        } catch (error) {
            showError('Error updating user');
            return null;
        }
    },

    onDelete: async (id: string) => {
        try {
            const response = await doDELETE(`${BASE}/${id}`);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
                showSuccess("User deleted successfully");
            } else {
                showError(response.message || 'Failed to delete user');
            }
        } catch (error) {
            showError('Error deleting user');
        }
    },

    bulkUploadUsers: async ({ file, corporateId }) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (corporateId) {
                formData.append('corporateId', corporateId);
            }

            const response = await doPOST(`${BASE}/bulk`, formData);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
                const result = response?.data?.data || null;
                const createdCount = result?.created?.length || 0;
                const skippedCount = result?.skipped?.length || 0;
                const failedCount = result?.failed?.length || 0;
                showSuccess(
                    `Bulk upload completed. Created: ${createdCount}, skipped: ${skippedCount}, failed: ${failedCount}`
                );
                return result;
            }

            showError(response.message || 'Failed to upload users');
            return null;
        } catch (error) {
            showError('Error uploading users');
            return null;
        }
    },

    detail: async (id: string) => {
        try {
            const response = await doGET(`${BASE}/${id}`);
            return response.data;
        } catch (error) {
            showError('Failed to fetch user details');
            throw error;
        }
    },

    exportUsers: async () => {
        try {
            const { filters } = get();
            const exportParams = new URLSearchParams(filters as any);
            const response = await doGET(`${BASE}?${exportParams.toString()}`);

            if (!(response.status >= 200 && response.status < 400)) {
                showError(response.message || 'Failed to export users');
                return false;
            }

            const users = normalizeUsers(response.data);

            if (!users.length) {
                showError('No users found to export');
                return false;
            }

            const corporatesResponse = await doGET('/corporates');
            const corporates = corporatesResponse.status >= 200 && corporatesResponse.status < 400
                ? normalizeCorporates(corporatesResponse.data)
                : [];
            const corporateNameMap = new Map(
                corporates.map((corporate) => [
                    corporate._id,
                    corporate.companyName || corporate.name || '',
                ])
            );

            const couponUsageList = await Promise.all(
                users.map(async (user) => {
                    if (!user._id) {
                        return '';
                    }

                    try {
                        const couponResponse = await doGET(`/coupons/usage/user/${user._id}?page=1&limit=100`);
                        if (!(couponResponse.status >= 200 && couponResponse.status < 400)) {
                            return '';
                        }

                        return formatCouponUsage(couponResponse);
                    } catch (error) {
                        return '';
                    }
                })
            );

            downloadCsvFile(
                `users-export-${new Date().toISOString().slice(0, 10)}.csv`,
                [
                    'Name',
                    'Email',
                    'Phone',
                    'Corporate Name',
                    'Coupons',
                ],
                buildExportRows(users).map((row, index) => {
                    const updatedRow = [...row];
                    updatedRow.splice(3, 0, corporateNameMap.get(users[index].corporateId || '') || '');
                    updatedRow.splice(4, 0, couponUsageList[index] || '');
                    return updatedRow;
                })
            );

            showSuccess('Users exported successfully');
            return true;
        } catch (error) {
            showError('Failed to export users');
            return false;
        }
    },
}));

export const useUserStore = () => store((state) => state);
